import SparkMD5 from 'spark-md5';

import {BaseService} from './Core/BaseService';
import {FileUtils} from "../Utils/FileUtils";

class FileUploadService extends BaseService {
    get Url() { return super.Url + "/fileUploads"; }

    GetFileUpload(fileUploadId) {
        return this.SendRequest(BaseService.GET, fileUploadId);
    }

    GetFileDownloadLink(fileUploadObject) {
        return `https://${window.location.hostname}/files/download/${fileUploadObject.shortHash ?? fileUploadObject.fileUploadId}`;
    }

    DownloadFile(fileName, fileUploadId) {
        const request = { fileUploadId: fileUploadId };

        return this.SendRequest(BaseService.GET, request)
            .then(x => {
                if (x.url) {
                    let anchor = document.createElement("a");
                    anchor.href = x.url;
                    anchor.download = fileName;
                    anchor.click();
                    return x.file;
                } else {
                    throw Error('Unexpected response from StreamBank API.');
                }
            })
            .catch(error => {
                throw Error(error.message ?? 'This file is not available because an unexpected error occurred.');
            });
    }

    UpdateFile(file) {
        return this.SendRequest(BaseService.PUT, file);
    }

    // Called once the chunked upload lands, so the server records the file's
    // authoritative size from cloud storage. No activity is emitted for it.
    Finalize(fileUploadId) {
        return this.SendRequest(BaseService.POST, { fileUploadId }, null, '/finalize');
    }

    DeleteFile(fileId) {
        return this.SendRequest(BaseService.DELETE, fileId);
    }

    UploadFile(filename, file, contextNodeIds, updateCallback = null, surveyTypeId = null, tagCode = null) {
        return new Promise((resolve, reject) => {
            const errorHandler = message => e => {
                if(message)
                    throw Error(message);

                reject(e); //reject the promise.
            };

            //build the md5 hash of the file in chunks.
            this._GetMd5Hash(file)
                .then(md5 => {
                    const request = {
                        filename,
                        contextNodeIds,
                        hash: md5,
                        surveyTypeId,
                        tagCode
                    };

                    //Create file record in our db, and get a signed resumable url from the cloud storage provider
                    this.SendRequest(BaseService.POST, request)
                        //send our file in chunks to the signed url.
                        .then(x => {
                            const uploadedFile = x?.result?.file;

                            // Tell the server the object is in cloud storage so it
                            // records the authoritative size. The upload itself has
                            // already succeeded by this point — if finalize fails,
                            // resolve anyway and let the file_size_sync backstop
                            // pick the size up later.
                            const finalize = () => {
                                if(!uploadedFile?.fileUploadId) {
                                    resolve(uploadedFile);
                                    return;
                                }
                                this.Finalize(uploadedFile.fileUploadId)
                                    // finalize returns file:false when it declined to
                                    // write (missing object) — || falls back where ??
                                    // would pass the false through
                                    .then(f => resolve(f?.result?.file || uploadedFile))
                                    .catch(() => resolve(uploadedFile));
                            };

                            if(x?.result?.url) { //Returns false if the file already exists in the cloud.
                                this._SendChunksToSignedUrl(x.result.url, file, md5, updateCallback)
                                    .then(finalize)
                                    .catch(errorHandler('File upload failed during transmission.'));
                            } else {
                                //The object is already in the cloud, so skip sending
                                //chunks — but still finalize: this heals a row whose
                                //original uploader died before finalizing.
                                updateCallback(1,1);
                                finalize();
                            }
                        })
                        .catch(errorHandler('File upload failed to start.'));
                })
                .catch(errorHandler('Unable to access the specified file.'));
        });
    }

    _SendChunksToSignedUrl(url, file, md5, updateCallback = null) {
        //If we want to speed things up, it sounds like gcloud would support multiple chunks being uploaded at the same time. This would require this to be redone a bit, and
        // we'd need to play around with how many uploads vs chunksize, but we can really improve things if needed.
        return new Promise((resolve, reject) => {
            const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice; //get blobSlice. I think we can just use slice from the polyfil.
            const chunkSize = 2097152; // Read in chunks of 2MB
            const chunks = Math.ceil(file.size / chunkSize); //get total # of chunks in file
            const fileReader = new FileReader();
            let currentChunk = 0, start = 0, end = 0; // current position

            const loadNext = (startRange = null) => {
                start = startRange ?? (currentChunk * chunkSize);
                end = Math.min(file.size, (start + chunkSize));

                //read a slice of the file content for this chunk.
                fileReader.readAsArrayBuffer(file.slice(start, end));
            };

            //on single chunk load callback.
            fileReader.onload = e => {
                currentChunk++;

                const xhr = new XMLHttpRequest();

                //open against the signed url with a put.
                xhr.open(BaseService.PUT, url, true);

                //add headers for chunk size. and chunk position
                xhr.setRequestHeader("Content-Range", "bytes " + start + "-" + (end - 1) + "/" + file.size);
                //TODO: add google header for md5 in final chunk so they can verify server side.
                /*
                if(end === file.size) {
                    xhr.setRequestHeader("LOOKUP", md5);
                }
                */

                //on the response either send the next chunk or resolve.
                xhr.onload = _ => {
                    if(xhr.status >= 200 && xhr.status < 300) {//Anything in the 200s is good.
                        if(currentChunk < chunks) {
                            // We should in theory be done according to the server, so stop uploading
                            console.log("signed url upload thinks we're done, but we still have chunks?", currentChunk, chunks);
                        }
                        updateCallback?.(chunks, chunks);
                        resolve();
                    } else if (xhr.status === 308) {
                        //we have more to upload.
                        updateCallback?.(currentChunk, chunks);

                        const { start, end } = FileUtils.ParseHTTPRangeHeader(xhr.getResponseHeader('Range'));
                        loadNext(end ?? null);
                    } else {
                        //TODO: There are some 500 cases and stuff we can retry and make things less fragile.
                        reject();
                    }
                };
                //handle errors
                xhr.onerror = e => reject({message: "could not upload file in chunks", file: file, err: e});
                //send data
                xhr.send(e.target.result);
            };
            //on error reading any chunk
            fileReader.onerror = e => reject({message: "could not read file chunks for upload", file: file, err: e});

            updateCallback?.(currentChunk, chunks);

            //lets get this rolling.
            loadNext();
        });
    }

    _GetMd5Hash(file) {
        return new Promise((resolve, reject) => {
            const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice;
            const chunkSize = 2097152; // Read in chunks of 2MB
            const chunks = Math.ceil(file.size / chunkSize);
            const spark = new SparkMD5.ArrayBuffer();
            const fileReader = new FileReader();
            let currentChunk = 0;

            const loadNext = _ => {
                const start = currentChunk * chunkSize;
                const end = ((start + chunkSize) >= file.size) ? file.size : start + chunkSize;

                fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
            };

            fileReader.onload = e => {
                spark.append(e.target.result); // Append array buffer
                currentChunk++;

                (currentChunk < chunks) ? loadNext(): resolve(spark.end());
            };
            fileReader.onerror = e => reject({message: "could not generate md5 sum", file: file, err: e});

            loadNext();
        });
    }
}

export const service = new FileUploadService();

